import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Brain,
  CheckCircle2,
  Clock3,
  Inbox,
  Mail,
  Search,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import MailImportConsentModal from "../components/MailImportConsentModal";

const DEFAULT_AGENT_GOAL = "Find job-related CVs from email, stage them for approval, then scan approved candidates.";
const ACTIVE_INBOX_JOB_KEY = "inboxToShortlistActiveJobId";

export default function InboxToShortlist() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const consentResolverRef = useRef(null);
  const progressRef = useRef(null);
  const stagedReviewRef = useRef(null);
  const enrichmentPollIntervalRef = useRef(null);

  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(searchParams.get("job_id") || localStorage.getItem(ACTIVE_INBOX_JOB_KEY) || "");
  const [provider, setProvider] = useState("outlook");
  const [mailboxScope, setMailboxScope] = useState("inbox");
  const [mailboxFolderName, setMailboxFolderName] = useState("");
  const [lookbackDays, setLookbackDays] = useState(14);
  const [maxResults, setMaxResults] = useState(25);
  const [agentGoal, setAgentGoal] = useState(DEFAULT_AGENT_GOAL);
  const [agentPlan, setAgentPlan] = useState([]);
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [alreadyRejectedCandidates, setAlreadyRejectedCandidates] = useState([]);
  const [selectedStagingIds, setSelectedStagingIds] = useState([]);
  const [agentReport, setAgentReport] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [approvingAndScanning, setApprovingAndScanning] = useState(false);
  const [rejectingCandidates, setRejectingCandidates] = useState(false);
  const [processStartedAt, setProcessStartedAt] = useState(null);
  const [processElapsedSeconds, setProcessElapsedSeconds] = useState(0);
  const [enrichmentProgress, setEnrichmentProgress] = useState({
    active: false,
    enriched: 0,
    total: 0,
    topScanIds: [],
  });
  const [mailImportConsent, setMailImportConsent] = useState({
    open: false,
    provider: "outlook",
  });

  const hasInboxToShortlistAccess = ["pro", "pilot", "enterprise"].includes(user?.plan);
  useLongActionNotice(connecting, "Email mailbox connection", 30000);
  useLongActionNotice(importing, "Inbox to Shortlist import", 45000);
  useLongActionNotice(approvingAndScanning, "Approve and scan", 45000);
  useLongActionNotice(rejectingCandidates, "Reject staged candidates", 30000);

  const isWorkflowRunning = connecting || importing || approvingAndScanning || enrichmentProgress.active;

  const selectedJob = useMemo(
    () => jobs.find((job) => String(job.id) === String(jobId)),
    [jobs, jobId]
  );

  const formatEmailImportDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatElapsedTime = (totalSeconds = 0) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const hasAiAnalysis = (candidate = {}) => (
    Boolean(candidate.llm_enriched) ||
    Boolean(candidate.ai_summary) ||
    (candidate.llm_strengths || []).length > 0 ||
    (candidate.llm_gaps || []).length > 0 ||
    (candidate.skill_evidence || []).length > 0 ||
    (candidate.language_match || []).length > 0
  );

  const updateCandidateEnrichment = (status) => {
    if (!status?.scan_id) return;

    setScanResult((current) => {
      if (!current) return current;

      const mergeCandidate = (candidate) => (
        candidate.scan_id === status.scan_id
          ? {
              ...candidate,
              ...status,
              candidate_summary: {
                ...(candidate.candidate_summary || {}),
                ai_summary: status.ai_summary,
                llm_strengths: status.llm_strengths || [],
                llm_gaps: status.llm_gaps || [],
                interview_questions: status.interview_questions || [],
                skill_evidence: status.skill_evidence || [],
                language_match: status.language_match || [],
              },
            }
          : candidate
      );

      return {
        ...current,
        top_candidates: (current.top_candidates || []).map(mergeCandidate),
        ranked_candidates: (current.ranked_candidates || []).map(mergeCandidate),
        remaining_candidates: (current.remaining_candidates || []).map(mergeCandidate),
      };
    });
  };

  const topAiEnrichmentComplete = () => {
    const topCandidates = scanResult?.top_candidates || [];
    return topCandidates.length > 0 && topCandidates.every((candidate) => hasAiAnalysis(candidate));
  };

  const stopEnrichmentPolling = () => {
    if (enrichmentPollIntervalRef.current) {
      clearInterval(enrichmentPollIntervalRef.current);
      enrichmentPollIntervalRef.current = null;
    }
  };

  const startEnrichmentPolling = (topScanIds = []) => {
    stopEnrichmentPolling();

    if (!topScanIds.length) {
      setEnrichmentProgress({ active: false, enriched: 0, total: 0, topScanIds: [] });
      return;
    }

    setEnrichmentProgress({
      active: true,
      enriched: 0,
      total: topScanIds.length,
      topScanIds,
    });

    const poll = async () => {
      try {
        const res = await apiFetch(`/scans/enrichment-status?ids=${encodeURIComponent(topScanIds.join(","))}`);
        const data = await res.json();
        const statuses = data?.statuses || [];
        const enrichedStatuses = statuses.filter((status) => status?.llm_enriched);

        enrichedStatuses.forEach(updateCandidateEnrichment);

        const complete = enrichedStatuses.length >= topScanIds.length;
        setEnrichmentProgress({
          active: !complete,
          enriched: enrichedStatuses.length,
          total: topScanIds.length,
          topScanIds,
        });

        if (complete) {
          stopEnrichmentPolling();
        }
      } catch {
        setEnrichmentProgress((current) => ({
          ...current,
          active: false,
        }));
        stopEnrichmentPolling();
      }
    };

    poll();
    enrichmentPollIntervalRef.current = setInterval(poll, 3500);
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isWorkflowRunning) {
      setProcessStartedAt(null);
      setProcessElapsedSeconds(0);
      return;
    }

    setProcessStartedAt((startedAt) => startedAt || Date.now());
  }, [isWorkflowRunning]);

  useEffect(() => {
    if (!processStartedAt || !isWorkflowRunning) return;

    const timer = setInterval(() => {
      setProcessElapsedSeconds(Math.max(0, Math.floor((Date.now() - processStartedAt) / 1000)));
    }, 1000);

    return () => clearInterval(timer);
  }, [processStartedAt, isWorkflowRunning]);

  useEffect(() => () => stopEnrichmentPolling(), []);

  const fetchJobs = async () => {
    try {
      const res = await apiFetch("/jobs");
      const data = await res.json();

      if (!res.ok) {
        toast.error(data?.detail || "Failed to load jobs.");
        return;
      }

      setJobs(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load jobs.");
    } finally {
      setFetching(false);
    }
  };

  const fetchPendingCandidates = async (selectedJobId = jobId) => {
    if (!selectedJobId) {
      setPendingCandidates([]);
      setSelectedStagingIds([]);
      return;
    }

    try {
      const res = await apiFetch(`/candidates/import-staging?job_id=${selectedJobId}`);
      const data = await res.json();

      if (!res.ok) return;

        setPendingCandidates(data || []);
        setSelectedStagingIds((data || []).map((item) => item.staging_id).filter(Boolean));
        if ((data || []).length) {
          localStorage.setItem(ACTIVE_INBOX_JOB_KEY, String(selectedJobId));
        }
    } catch {
      console.error("Failed to load staged email candidates");
    }
  };

  const fetchAgentPlan = async (selectedJobId = jobId, selectedProvider = provider) => {
    if (!selectedJobId || !hasInboxToShortlistAccess) {
      setAgentPlan([]);
      return;
    }

    try {
      const res = await apiFetch("/inbox-to-shortlist/plan", {
        method: "POST",
        body: JSON.stringify({
          job_id: Number(selectedJobId),
          provider: selectedProvider,
          mailbox_scope: mailboxScope,
          mailbox_folder_name: mailboxFolderName || undefined,
          lookback_days: Number(lookbackDays) || 14,
        }),
      });
      const data = await res.json();

      if (!res.ok) return;

      setAgentPlan(data.plan || []);
    } catch {
      console.error("Failed to load Inbox-to-Shortlist plan");
    }
  };

  const requestMailboxConsent = (selectedProvider) =>
    new Promise((resolve) => {
      consentResolverRef.current = resolve;
      setMailImportConsent({
        open: true,
        provider: selectedProvider,
      });
    });

  const closeMailboxConsent = (accepted) => {
    const resolve = consentResolverRef.current;
    consentResolverRef.current = null;
    setMailImportConsent((prev) => ({
      ...prev,
      open: false,
    }));
    resolve?.(accepted);
  };

  const confirmMailboxConsentIfNeeded = async () => {
    const settingsRes = await apiFetch(`/candidate-import/${provider}/settings`);
    const settingsData = await settingsRes.json();

    if (!settingsRes.ok) {
      toast.error(settingsData?.detail || "Could not verify mailbox consent.");
      return false;
    }

    if (!settingsData.mailbox_consent_required) {
      return true;
    }

    const accepted = await requestMailboxConsent(provider);
    if (!accepted) {
      return false;
    }

    const consentRes = await apiFetch(`/candidate-import/${provider}/consent`, {
      method: "POST",
    });
    const consentData = await consentRes.json();

    if (!consentRes.ok) {
      toast.error(consentData?.detail || "Could not save mailbox consent.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!user) return;
    fetchJobs();
  }, [user]);

  useEffect(() => {
    if (!user || !jobId) return;
    fetchPendingCandidates(jobId);
    fetchAgentPlan(jobId, provider);
  }, [user, jobId, provider, lookbackDays, mailboxScope, mailboxFolderName]);

  useEffect(() => {
    if (!user || !hasInboxToShortlistAccess) return;

    const code = searchParams.get("code");
    const senderProvider = localStorage.getItem("candidateEmailConnectProvider");
    const savedProvider = localStorage.getItem("inboxToShortlistProvider");

    if (!code || !savedProvider) {
      if (!code || !senderProvider) return;

      const completeSenderConnection = async () => {
        try {
          const res = await apiFetch(`/candidate-email-settings/connect/${senderProvider}/complete`, {
            method: "POST",
            body: JSON.stringify({ code }),
          });
          const data = await res.json();

          localStorage.removeItem("candidateEmailConnectProvider");

          if (!res.ok) {
            toast.error(actionFailureMessage("Candidate email sender connection", data?.detail));
          } else {
            toast.success(`${senderProvider === "gmail" ? "Gmail" : "Outlook"} sender connected.`);
          }
        } catch {
          toast.error(actionFailureMessage("Candidate email sender connection"));
        } finally {
          navigate("/profile?section=app_configuration", { replace: true });
        }
      };

      completeSenderConnection();
      return;
    }

    const runEmailAgent = async () => {
      setImporting(true);
      setProcessStartedAt(Date.now());
      setProcessElapsedSeconds(0);
      setTimeout(() => progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

      const savedJobId = localStorage.getItem("inboxToShortlistJobId");
      const savedGoal = localStorage.getItem("inboxToShortlistGoal") || DEFAULT_AGENT_GOAL;
      const savedLookbackDays = Number(localStorage.getItem("inboxToShortlistLookbackDays") || 14);
      const savedMaxResults = Number(localStorage.getItem("inboxToShortlistMaxResults") || 25);
      const savedMailboxScope = localStorage.getItem("inboxToShortlistMailboxScope") || "inbox";
      const savedMailboxFolderName = localStorage.getItem("inboxToShortlistMailboxFolderName") || "";

      try {
        const res = await apiFetch(`/candidate-import/${savedProvider}/agent-import`, {
          method: "POST",
          body: JSON.stringify({
            code,
            goal: savedGoal,
            job_id: Number(savedJobId),
            mailbox_scope: savedMailboxScope,
            mailbox_folder_name: savedMailboxFolderName || undefined,
            lookback_days: savedLookbackDays,
            max_results: savedMaxResults,
          }),
        });
        const data = await res.json();

        if (!res.ok) {
          toast.error(actionFailureMessage("Inbox to Shortlist import", data?.detail));
          return;
        }

        const stagedCandidates = data.tool_result?.staged || [];
        const rejectedCandidates = data.tool_result?.already_rejected || [];
        setAgentReport(data);
        setPendingCandidates(stagedCandidates);
        setAlreadyRejectedCandidates(rejectedCandidates);
        setSelectedStagingIds(stagedCandidates.map((item) => item.staging_id).filter(Boolean));
        setJobId(savedJobId || "");
        localStorage.setItem(ACTIVE_INBOX_JOB_KEY, String(savedJobId || ""));
        toast.success(`Found ${data.summary?.staged_count || 0} candidate CVs for approval.`);
        window.history.replaceState({}, "", `/inbox-to-shortlist?job_id=${savedJobId}`);
      } catch {
        toast.error(actionFailureMessage("Inbox to Shortlist import"));
      } finally {
        setImporting(false);
        [
          "inboxToShortlistProvider",
          "inboxToShortlistJobId",
          "inboxToShortlistGoal",
          "inboxToShortlistLookbackDays",
          "inboxToShortlistMaxResults",
          "inboxToShortlistMailboxScope",
          "inboxToShortlistMailboxFolderName",
          "emailImportProvider",
          "emailImportJobId",
          "emailImportAgentGoal",
          "emailImportLookbackDays",
          "emailImportMailboxScope",
          "emailImportMailboxFolderName",
          "emailImportReturnPath",
        ].forEach((key) => localStorage.removeItem(key));
      }
    };

    runEmailAgent();
  }, [user, hasInboxToShortlistAccess, searchParams]);

  const handleConnectEmail = async () => {
    if (!hasInboxToShortlistAccess) {
      toast.error("Upgrade to Pro, Pilot, or Enterprise to use Inbox to Shortlist.");
      return;
    }

    if (!jobId) {
      toast.error("Select a job before running Inbox to Shortlist.");
      return;
    }

    if (mailboxScope === "folder" && !mailboxFolderName.trim()) {
      toast.error("Enter the folder or label name before importing from a specific mail folder.");
      return;
    }

    setConnecting(true);
    setPendingCandidates([]);
    setAlreadyRejectedCandidates([]);
    setSelectedStagingIds([]);
    setProcessStartedAt(Date.now());
    setProcessElapsedSeconds(0);
    setTimeout(() => progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

    try {
      const consentAccepted = await confirmMailboxConsentIfNeeded();
      if (!consentAccepted) return;

      localStorage.removeItem("candidateEmailConnectProvider");
      localStorage.setItem("inboxToShortlistProvider", provider);
      localStorage.setItem("inboxToShortlistJobId", String(jobId));
      localStorage.setItem("inboxToShortlistGoal", agentGoal || DEFAULT_AGENT_GOAL);
      localStorage.setItem("inboxToShortlistLookbackDays", String(lookbackDays));
      localStorage.setItem("inboxToShortlistMaxResults", String(maxResults));
      localStorage.setItem("inboxToShortlistMailboxScope", mailboxScope);
      localStorage.setItem("inboxToShortlistMailboxFolderName", mailboxFolderName || "");

      // These keys let the older Candidates-page OAuth callback safely finish
      // the staging step if the current OAuth redirect still points there.
      localStorage.setItem("emailImportProvider", provider);
      localStorage.setItem("emailImportJobId", String(jobId));
      localStorage.setItem("emailImportAgentGoal", agentGoal || DEFAULT_AGENT_GOAL);
      localStorage.setItem("emailImportLookbackDays", String(lookbackDays));
      localStorage.setItem("emailImportMailboxScope", mailboxScope);
      localStorage.setItem("emailImportMailboxFolderName", mailboxFolderName || "");
      localStorage.setItem("emailImportReturnPath", "inbox-to-shortlist");

      const res = await apiFetch(`/candidate-import/${provider}/connect`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Email mailbox connection", data?.detail));
        return;
      }

      window.location.href = data.auth_url;
    } catch {
      toast.error(actionFailureMessage("Email mailbox connection"));
    } finally {
      setConnecting(false);
    }
  };

  const handleApproveAndScan = async () => {
    if (!selectedStagingIds.length) {
      toast.error("Select at least one staged candidate to approve and scan.");
      return;
    }

    setApprovingAndScanning(true);
    setScanResult(null);
    setEnrichmentProgress({ active: false, enriched: 0, total: 0, topScanIds: [] });
    setProcessStartedAt(Date.now());
    setProcessElapsedSeconds(0);
    setTimeout(() => progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

    try {
      const res = await apiFetch("/inbox-to-shortlist/approve-and-scan", {
        method: "POST",
        body: JSON.stringify({
          job_id: Number(jobId),
          staging_ids: selectedStagingIds,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Approve and scan candidates", data?.detail));
        return;
      }

      setScanResult(data.scan);
      const topScanIds = (data.scan?.top_candidates || [])
        .map((candidate) => candidate.scan_id)
        .filter(Boolean);
      startEnrichmentPolling(topScanIds);
      setPendingCandidates((prev) => prev.filter((item) => !selectedStagingIds.includes(item.staging_id)));
      setSelectedStagingIds([]);
      toast.success("Inbox-to-Shortlist scan completed.");
    } catch {
      toast.error(actionFailureMessage("Approve and scan candidates"));
    } finally {
      setApprovingAndScanning(false);
    }
  };

  const handleRejectStagedCandidates = async () => {
    if (!selectedStagingIds.length) {
      toast.error("Select at least one staged candidate to reject.");
      return;
    }

    setRejectingCandidates(true);

    try {
      const res = await apiFetch("/candidates/import-staging/reject", {
        method: "POST",
        body: JSON.stringify({ staging_ids: selectedStagingIds }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Reject staged candidates", data?.detail));
        return;
      }

      const rejectedItems = pendingCandidates
        .filter((item) => selectedStagingIds.includes(item.staging_id))
        .map((item) => ({
          ...item,
          reason: "This CV was already rejected for this job, so it was kept out of the approval queue.",
        }));

      setPendingCandidates((prev) => prev.filter((item) => !selectedStagingIds.includes(item.staging_id)));
      setAlreadyRejectedCandidates((prev) => [...rejectedItems, ...prev]);
      setSelectedStagingIds([]);
      toast.success(`Rejected ${data.rejected_count || 0} staged candidate${data.rejected_count === 1 ? "" : "s"}.`);
    } catch {
      toast.error(actionFailureMessage("Reject staged candidates"));
    } finally {
      setRejectingCandidates(false);
    }
  };

  const toggleStagingSelection = (stagingId) => {
    setSelectedStagingIds((prev) => (
      prev.includes(stagingId)
        ? prev.filter((id) => id !== stagingId)
        : [...prev, stagingId]
    ));
  };

  const getApplicationQualityClass = (quality) => {
    switch (quality?.status) {
      case "needs_review":
        return "border-rose-200 bg-rose-50 text-rose-700";
      case "check_details":
        return "border-amber-200 bg-amber-50 text-amber-800";
      default:
        return "border-emerald-200 bg-emerald-50 text-emerald-800";
    }
  };

  const getWorkflowStageState = () => {
    if (connecting) {
      return {
        activeKey: "connect",
        progress: 15,
        title: "Connecting mailbox",
        subtitle: `Opening ${provider === "outlook" ? "Outlook" : "Gmail"} permission flow for this job.`,
      };
    }

    if (importing) {
      return {
        activeKey: "import",
        progress: 45,
        title: "Searching inbox for CVs",
        subtitle: "Checking recent job-related emails, validating PDF attachments, and staging matching applicants.",
      };
    }

    if (pendingCandidates.length > 0 && !scanResult && !approvingAndScanning) {
      return {
        activeKey: "approval",
        progress: 62,
        variant: "review",
        title: "Candidates ready for approval",
        subtitle: `${pendingCandidates.length} staged candidate${pendingCandidates.length === 1 ? "" : "s"} found. Approve the right applicants before scanning.`,
      };
    }

    if (approvingAndScanning) {
      return {
        activeKey: "scan",
        progress: 76,
        title: "Approving and ranking candidates",
        subtitle: "Saving approved applicants, running the scan engine, and preparing ranked results.",
      };
    }

    if (scanResult && enrichmentProgress.active) {
      return {
        activeKey: "enrich",
        progress: 88,
        title: "AI enriching top candidates",
        subtitle: `${enrichmentProgress.enriched} / ${enrichmentProgress.total} top candidates enriched. Results can be reviewed while AI details finish.`,
      };
    }

    if (scanResult && enrichmentProgress.total > 0 && enrichmentProgress.enriched < enrichmentProgress.total) {
      return {
        activeKey: "enrich",
        progress: 94,
        title: "Ranking ready, AI enrichment still finishing",
        subtitle: `${enrichmentProgress.enriched} / ${enrichmentProgress.total} top candidates enriched. Open Rankings any time to continue review.`,
      };
    }

    if (scanResult) {
      return {
        activeKey: "complete",
        progress: 100,
        title: "Inbox to Shortlist complete",
        subtitle: "Candidates are ranked and ready for recruiter review.",
      };
    }

    return null;
  };

  const renderWorkflowProgress = () => {
    const stageState = getWorkflowStageState();
    if (!stageState) return null;

    const stages = [
      { key: "connect", label: "Connect" },
      { key: "import", label: "Find CVs" },
      { key: "approval", label: "Approve" },
      { key: "scan", label: "Rank" },
      { key: "enrich", label: "AI enrich" },
      { key: "complete", label: "Complete" },
    ];
    const activeIndex = stages.findIndex((stage) => stage.key === stageState.activeKey);
    const isReviewStage = stageState.variant === "review";
    const panelClass = isReviewStage
      ? "scroll-mt-24 overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-br from-amber-950 via-emerald-950 to-slate-950 p-5 text-white shadow-lg"
      : "scroll-mt-24 overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-950 p-5 text-white shadow-lg";
    const iconClass = isReviewStage
      ? "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-amber-200/40 bg-amber-300/20"
      : "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-300/30 bg-emerald-400/15";
    const progressClass = isReviewStage
      ? "h-full rounded-full bg-gradient-to-r from-amber-300 via-lime-200 to-emerald-200 shadow-[0_0_18px_rgba(251,191,36,0.55)] transition-all duration-700 ease-out"
      : "h-full rounded-full bg-gradient-to-r from-emerald-300 via-lime-200 to-emerald-100 shadow-[0_0_18px_rgba(110,231,183,0.55)] transition-all duration-700 ease-out";
    const percentClass = isReviewStage
      ? "w-fit rounded-full border border-amber-200/50 bg-amber-100 px-4 py-2 text-sm font-bold text-amber-950 shadow-sm"
      : "w-fit rounded-full border border-emerald-300/40 bg-white px-4 py-2 text-sm font-bold text-emerald-900 shadow-sm";

    return (
      <div ref={progressRef} className={panelClass}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={iconClass}>
              {isReviewStage ? (
                <CheckCircle2 className="animate-pulse text-amber-200" size={24} />
              ) : (
                <Brain className={isWorkflowRunning ? "animate-pulse text-emerald-200" : "text-emerald-200"} size={24} />
              )}
            </div>
            <div>
              <p className="text-base font-semibold">{stageState.title}</p>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-50/80">{stageState.subtitle}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-emerald-50">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  {selectedJob?.name || "Selected job"}
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  {mailboxScope === "folder" && mailboxFolderName
                    ? `Folder: ${mailboxFolderName}`
                    : mailboxScope === "all"
                      ? "Inbox and folders"
                      : `${provider === "outlook" ? "Outlook" : "Gmail"} inbox`}
                </span>
                {isWorkflowRunning && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1">
                    <Clock3 size={13} className="text-emerald-200" />
                    Running {formatElapsedTime(processElapsedSeconds)}
                  </span>
                )}
                {enrichmentProgress.total > 0 && (
                  <span className={`rounded-full border border-white/15 bg-white/10 px-3 py-1 ${
                    enrichmentProgress.active ? "animate-pulse" : ""
                  }`}>
                    {topAiEnrichmentComplete()
                      ? `${enrichmentProgress.total} / ${enrichmentProgress.total} AI enriched`
                      : `${enrichmentProgress.enriched} / ${enrichmentProgress.total} AI enriching`}
                  </span>
                )}
              </div>
            </div>
          </div>

          <span className={percentClass}>
            {stageState.progress}%
          </span>
        </div>

        <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/15">
          <div
            className={progressClass}
            style={{ width: `${stageState.progress}%` }}
          />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {stages.map((stage, index) => {
            const complete = index < activeIndex || stageState.activeKey === "complete";
            const active = index === activeIndex && stageState.activeKey !== "complete";

            return (
              <div
                key={stage.key}
                className={`flex min-h-[52px] items-center justify-center rounded-lg border px-3 text-center text-xs font-semibold transition ${
                  complete
                    ? "border-emerald-300/40 bg-emerald-300/15 text-emerald-50"
                    : active
                    ? isReviewStage
                      ? "animate-pulse border-amber-200 bg-amber-200 text-amber-950 shadow-[0_0_18px_rgba(251,191,36,0.45)]"
                      : "animate-pulse border-lime-200 bg-lime-200 text-emerald-950 shadow-[0_0_18px_rgba(190,242,100,0.35)]"
                    : "border-white/10 bg-white/5 text-emerald-50/45"
                }`}
              >
                <span>{stage.label}</span>
              </div>
            );
          })}
        </div>

        {isReviewStage && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-100 p-4 text-amber-950 shadow-[0_0_24px_rgba(251,191,36,0.22)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-base font-bold">Recruiter approval is needed.</p>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-amber-900">
                  The inbox search has staged likely CVs. Review the candidates below and approve only the ones that should enter ranking.
                </p>
              </div>
              <button
                type="button"
                onClick={() => stagedReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg bg-amber-950 px-4 text-sm font-semibold text-white transition hover:bg-amber-900"
              >
                Review staged CVs
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading || fetching || !user) {
    return <p className="mt-20 text-center">Loading...</p>;
  }

  const leftPanel = (
    <RecruiterSidebar
      activeItem="inbox_to_shortlist"
      user={user}
      title="Recruiter Panel"
    />
  );

  const mainContent = (
    <div className="space-y-6">
      <MailImportConsentModal
        open={mailImportConsent.open}
        provider={mailImportConsent.provider}
        onCancel={() => closeMailboxConsent(false)}
        onConfirm={() => closeMailboxConsent(true)}
      />

      {!hasInboxToShortlistAccess && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
          Inbox to Shortlist is available on Pro, Pilot, and Enterprise plans. You can preview the workflow, but email import and scanning are locked until you upgrade.
        </section>
      )}

      <section className="overflow-hidden rounded-lg border border-emerald-900/20 bg-[#031b14] shadow-sm">
        <div className="flex flex-col gap-4 p-5 text-white lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
              <Inbox size={14} />
              Inbox agent
            </div>
            <h2 className="mt-4 text-xl font-semibold">Agent setup</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-50/80">
              Select a job, choose Gmail or Outlook, then let the agent find matching CVs. Candidates are staged first, so nothing is saved until you approve it.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 lg:w-[320px]">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-100/80">Select job</span>
              <select
                value={jobId}
                onChange={(event) => {
                  const nextJobId = event.target.value;
                  setJobId(nextJobId);
                  if (nextJobId) {
                    localStorage.setItem(ACTIVE_INBOX_JOB_KEY, nextJobId);
                  } else {
                    localStorage.removeItem(ACTIVE_INBOX_JOB_KEY);
                  }
                }}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-900/30"
              >
                <option value="">Select a job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.name}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={handleConnectEmail}
              disabled={!hasInboxToShortlistAccess || connecting || importing || !jobId}
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Mail size={16} />
              {connecting || importing ? "Running agent..." : "Find CVs from email"}
            </button>
          </div>
        </div>

        <div className="grid gap-4 border-t border-white/10 bg-white p-5 lg:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            Email provider
            <select
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="gmail" disabled>Gmail - in progress</option>
              <option value="outlook">Outlook / Microsoft 365</option>
            </select>
          </label>

          <label className="text-sm font-semibold text-slate-700">
            Search recent emails
            <select
              value={lookbackDays}
              onChange={(event) => setLookbackDays(Number(event.target.value))}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </label>

          <label className="text-sm font-semibold text-slate-700">
            Import source
            <select
              value={mailboxScope}
              onChange={(event) => setMailboxScope(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="inbox">Inbox only</option>
              <option value="folder">Specific folder/label</option>
              <option value="all">Inbox and folders</option>
            </select>
          </label>

          {mailboxScope === "folder" && (
            <label className="text-sm font-semibold text-slate-700">
              Folder / label name
              <input
                value={mailboxFolderName}
                onChange={(event) => setMailboxFolderName(event.target.value)}
                placeholder="Backend Applicants"
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
              />
            </label>
          )}

          <label className="text-sm font-semibold text-slate-700">
            Max email results
            <input
              type="number"
              min="1"
              max="25"
              value={maxResults}
              onChange={(event) => setMaxResults(Math.min(25, Math.max(1, Number(event.target.value) || 1)))}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            />
          </label>
        </div>

        <div className="border-t border-slate-100 bg-white p-5">
          <label className="block text-sm font-semibold text-slate-700">
            Agent goal
            <textarea
              value={agentGoal}
              onChange={(event) => setAgentGoal(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
            />
          </label>
        </div>
      </section>

      {renderWorkflowProgress()}

      <section ref={stagedReviewRef} className="scroll-mt-24 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-950">Workflow plan</h2>
        <div className="mt-5 grid gap-3 lg:grid-cols-5">
          {(agentPlan.length ? agentPlan : [
            { step: "Select job", detail: "Choose the job this inbox flow belongs to." },
            { step: "Find CVs", detail: "Connect email and search recent PDF attachments." },
            { step: "Approve", detail: "Review staged candidates before saving." },
            { step: "Bulk scan", detail: "Run the existing scan engine." },
            { step: "Review", detail: "Open rankings and decide." },
          ]).map((step, index) => (
            <div key={`${step.step}-${index}`} className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-800 text-sm font-bold text-white">
                {index + 1}
              </span>
              <h3 className="mt-3 text-sm font-semibold text-emerald-950">{step.step}</h3>
              <p className="mt-2 text-xs leading-5 text-emerald-800">{step.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {agentReport && (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <h2 className="font-semibold text-emerald-950">Agent report</h2>
          <p className="mt-2 text-sm leading-6 text-emerald-800">{agentReport.agent_summary}</p>
        </section>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Approve staged candidates</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              The agent found these likely CVs for {selectedJob?.name || "the selected job"}. Approve only the right applicants before scanning.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedStagingIds(pendingCandidates.map((item) => item.staging_id))}
              disabled={pendingCandidates.length === 0 || approvingAndScanning || rejectingCandidates}
              className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-emerald-200 bg-white px-4 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleRejectStagedCandidates}
              disabled={!selectedStagingIds.length || approvingAndScanning || rejectingCandidates}
              className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-rose-200 bg-white px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rejectingCandidates ? "Rejecting..." : "Reject selected"}
            </button>
            <button
              type="button"
              onClick={handleApproveAndScan}
              disabled={!selectedStagingIds.length || approvingAndScanning || rejectingCandidates}
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles size={16} />
              {approvingAndScanning ? "Approving and scanning..." : `Approve & scan ${selectedStagingIds.length}`}
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-3">
          {pendingCandidates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
              No staged email candidates are waiting for this job.
            </div>
          ) : (
            pendingCandidates.map((candidate) => (
              <label key={candidate.staging_id} className="flex cursor-pointer gap-3 rounded-lg border border-emerald-200 bg-white p-3">
                <input
                  type="checkbox"
                  checked={selectedStagingIds.includes(candidate.staging_id)}
                  onChange={() => toggleStagingSelection(candidate.staging_id)}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <p className="truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="w-fit rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                        {candidate.provider === "outlook" ? "Outlook" : "Gmail"}
                      </span>
                      {candidate.application_quality && (
                        <span className={`w-fit rounded-full border px-2 py-1 text-xs font-semibold ${getApplicationQualityClass(candidate.application_quality)}`}>
                          {candidate.application_quality.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 grid gap-1 rounded-lg border border-emerald-100 bg-emerald-50/70 p-3 text-xs leading-5 text-slate-600">
                    <p className="break-words">
                      <span className="font-semibold text-slate-800">From:</span> {candidate.source_email || "Unknown sender"}
                    </p>
                    <p className="break-words">
                      <span className="font-semibold text-slate-800">Subject:</span> {candidate.email_subject || "No subject"}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-800">Date:</span> {formatEmailImportDate(candidate.email_received_at || candidate.email_date || candidate.created_at)}
                    </p>
                  </div>
                  {candidate.application_quality?.reasons?.length > 0 && (
                    <details className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <summary className="cursor-pointer list-none text-xs font-semibold text-slate-700">
                        Application quality · {candidate.application_quality.label}
                        <span className="ml-2 text-slate-400">Show details</span>
                      </summary>
                      <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
                        {candidate.application_quality.reasons.map((reason, index) => (
                          <li key={`${candidate.staging_id}-quality-${index}`} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <p className="mt-2 text-xs leading-5 text-emerald-800">{candidate.relevance_reason}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {candidate.detected_email && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                        {candidate.detected_email}
                      </span>
                    )}
                    {candidate.top_qualification && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                        {candidate.top_qualification}
                      </span>
                    )}
                    {(candidate.detected_skills || []).slice(0, 5).map((skill) => (
                      <span key={`${candidate.staging_id}-${skill}`} className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
      </section>

      {alreadyRejectedCandidates.length > 0 && (
        <section className="rounded-lg border border-rose-200 bg-rose-50 p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-rose-950">Already rejected for this job</h2>
              <p className="mt-1 text-sm leading-6 text-rose-800">
                RecruitGlance found these CVs again, but kept them out of approval because they were rejected earlier for this job.
              </p>
            </div>
            <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-rose-800">
              {alreadyRejectedCandidates.length} found again
            </span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {alreadyRejectedCandidates.map((candidate) => (
              <div key={`already-rejected-${candidate.staging_id}-${candidate.filename}`} className="rounded-lg border border-rose-100 bg-white p-3 text-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className="truncate font-semibold text-slate-950">{candidate.filename}</p>
                  <span className="w-fit rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                    Not in approval queue
                  </span>
                </div>
                <div className="mt-2 grid gap-1 text-xs leading-5 text-slate-600">
                  <p className="break-words">
                    <span className="font-semibold text-slate-800">From:</span> {candidate.source_email || "Unknown sender"}
                  </p>
                  <p className="break-words">
                    <span className="font-semibold text-slate-800">Subject:</span> {candidate.email_subject || "No subject"}
                  </p>
                  <p>
                    <span className="font-semibold text-slate-800">Date:</span> {formatEmailImportDate(candidate.email_received_at || candidate.email_date || candidate.created_at)}
                  </p>
                </div>
                <p className="mt-2 text-xs leading-5 text-rose-700">{candidate.reason}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {scanResult && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Ranked results ready</h2>
              <p className="mt-1 text-sm text-slate-500">
                {scanResult.total || 0} candidates scanned for {scanResult.job?.name}.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/jobs/${jobId}/rankings`)}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900"
            >
              <Trophy size={16} />
              Open Rankings
            </button>
          </div>

          <div className="mt-5 grid gap-3">
            {(scanResult.top_candidates || []).map((candidate, index) => (
              <div key={candidate.scan_id} className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-950">#{index + 1} {candidate.filename}</p>
                    <p className="mt-1 text-xs text-emerald-800">
                      {(candidate.matching_skills || []).slice(0, 5).join(", ") || "No matching skills detected"}
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-emerald-900">
                    {Math.round(candidate.match_score || 0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Automation agent"
      title="Inbox to Shortlist"
      subtitle="Find CVs from email, approve the right candidates, and scan them without leaving the workflow."
      headerAction={
        <span className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800">
          <Inbox size={16} />
          Approval-safe agent
        </span>
      }
      leftPanel={leftPanel}
      mainContent={mainContent}
      rightPanel={
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="flex items-center gap-2 font-semibold text-slate-950">
            <CheckCircle2 size={18} className="text-emerald-600" />
            Why approval first?
          </h3>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            Email inboxes can contain bills, old CVs, or unrelated attachments. RecruitGlance stages candidates first so the recruiter stays in control.
          </p>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <p className="flex items-start gap-2"><Search size={16} className="mt-0.5 text-slate-400" /> Recent job-related PDFs only</p>
            <p className="flex items-start gap-2"><Users size={16} className="mt-0.5 text-slate-400" /> Approved CVs become candidates</p>
            <p className="flex items-start gap-2"><Trophy size={16} className="mt-0.5 text-slate-400" /> Existing scan engine ranks results</p>
          </div>
        </div>
      }
    />
  );
}
